import React from 'react';

import {recordEvent} from 'src/components/Analytics';
import Dropdown from 'src/components/Dropdown';
import {useTranslations} from 'src/components/TranslationsContext';
import {CsvRow, downloadCsvRows} from 'src/lib/downloadData';

import {chartDownloadFilename, useChartImageMenu} from './chartMenu';

type Props = {
  title: string;
  siteName: string;
  /** Called on click so the rows are only built when a download is requested. */
  getCsvRows: () => CsvRow[];
  children: React.ReactNode;
};

/**
 * Titles a chart drawn from plain arrays and offers it for download. Time series
 * charts use Viz instead, which wires up the richer VizMoreOption menu.
 */
export default function ChartFrame({title, siteName, getCsvRows, children}: Props) {
  const {t} = useTranslations();
  const [vizEl, setVizEl] = React.useState<HTMLDivElement | null>(null);

  const filename = () => chartDownloadFilename(`${title} for ${siteName}`);
  const imageMenu = useChartImageMenu({
    buildChart: () => vizEl && {vizEl, title: `${title} ${t('at')} ${siteName}`},
    filename,
    title,
  });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 text-sm">
        <div className="font-semibold">{title}</div>
        <Dropdown
          title={imageMenu.label}
          itemWidth={200}
          link={true}
          props={{className: 'relative shrink-0'}}
          items={[
            {
              props: {
                onClick: () => {
                  downloadCsvRows(getCsvRows(), `${filename()}.csv`);
                  recordEvent('download_csv_clicked', {title});
                },
              },
              text: t('downloadCSV'),
            },
            ...imageMenu.items,
          ]}
        />
      </div>
      <div ref={setVizEl} className="bg-white">
        {children}
      </div>
    </div>
  );
}
